<script lang="ts">
import type { NestedSelectMenuItem, NestedSelectMenuTheme } from './types';

export interface NestedSelectMenuItemContentProps {
	item: NestedSelectMenuItem;
	index?: number;
	ui: NestedSelectMenuTheme;
	isRtl?: boolean;
	showChevron?: boolean;
}
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { useAppConfig } from '#app/config';

import { hasChildren } from './utils';

const props = withDefaults(defineProps<NestedSelectMenuItemContentProps>(), {
	index: 0,
	isRtl: false,
	showChevron: true,
});

defineSlots<{
	'item'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
	'item-leading'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
	'item-label'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
	'item-trailing'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
}>();

const appConfig = useAppConfig();

const chevronIcon = computed(() =>
	props.isRtl ? appConfig.ui.icons.chevronLeft : appConfig.ui.icons.chevronRight,
);
</script>

<template>
	<slot name="item" :item :index>
		<slot name="item-leading" :item :index>
			<DIcon
				v-if="item.icon"
				:name="item.icon"
				data-slot="itemLeadingIcon"
				:class="ui.itemLeadingIcon({ class: item.ui?.itemLeadingIcon })"
			/>
		</slot>

		<span
			data-slot="itemWrapper"
			:class="ui.itemWrapper({ class: item.ui?.itemWrapper })"
		>
			<span
				data-slot="itemLabel"
				:class="ui.itemLabel({ class: item.ui?.itemLabel })"
			>
				<slot name="item-label" :item :index>
					{{ item.label }}
				</slot>
			</span>
		</span>

		<span
			data-slot="itemTrailing"
			:class="ui.itemTrailing({ class: item.ui?.itemTrailing })"
		>
			<slot name="item-trailing" :item :index />

			<DIcon
				v-if="showChevron && hasChildren(item)"
				:name="chevronIcon"
				data-slot="itemTrailingIcon"
				:class="ui.itemTrailingIcon({ class: item.ui?.itemTrailingIcon })"
			/>
		</span>
	</slot>
</template>
